import { useUser } from '@plato/app-core/hooks/use-user';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { mergeFilterOptions } from '@plato/ui-legacy/components/filters/lib/merge-filter-options';
import type { VerticalFilterOption } from '@plato/ui-legacy/components/filters/types/filter-types';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import { VerticalFilterTemplate } from '../../filters/templates/vertical-filter-template.tsx';
import type { DraftListQueryFilters } from '../hooks/use-draft-list-applied-filters.tsx';
import { defaultDraftFilters, useDraftListAppliedFilters } from '../hooks/use-draft-list-applied-filters.tsx';

export function DraftListFilters() {
    const { t } = useTranslation('sales-draft');
    const user = useUser();
    const sendEvent = useAnalytics();

    const [appliedFilters, setAppliedFilters] = useDraftListAppliedFilters();
    const [modalFilters, setModalFilters] = useState<DraftListQueryFilters>(appliedFilters);

    const { data: filterOptions, isLoading } =
        api.platoUser.query.salesDraft.getSalesDraftList.getFilterOptions.useQuery(modalFilters, {
            placeholderData: keepPreviousData,
        });

    const { data: filterOptionsWithoutSearch } =
        api.platoUser.query.salesDraft.getSalesDraftList.getFilterOptions.useQuery(
            { ...modalFilters, search_query: '' },
            { placeholderData: keepPreviousData },
        );

    const getDraftStatusLabel = (status: string): string => {
        switch (status) {
            case 'NEW':
                return t('sales_draft__status__NEW', { ns: 'sales-draft', defaultValue: status });
            case 'SUBMITTED':
                return t('sales_draft__status__SUBMITTED', { ns: 'sales-draft', defaultValue: status });
            default:
                return status;
        }
    };

    // Ordered by how many distinct users actually apply each filter (PostHog, 180d).
    const filtersConfig: VerticalFilterOption<DraftListQueryFilters>[] = [
        {
            id: 'states',
            label: t('sales_draft__list_view__filter_states'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.states.options,
                filterOptionsWithoutSearch?.states.options,
                (o) => ({
                    id: o.option_id,
                    label: getDraftStatusLabel(o.option_id),
                    value: o.option_id,
                    count: o.drafts_quantity,
                }),
                appliedFilters.states,
            ),
        },
        {
            id: 'users',
            label: t('sales_draft__list_view__filter_users'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.users.options,
                filterOptionsWithoutSearch?.users.options,
                (o) => ({
                    id: o.option_id,
                    label: o.option || o.option_id,
                    value: o.option_id,
                    count: o.drafts_quantity,
                }),
                appliedFilters.users,
            ),
        },
        {
            id: 'customers',
            label: t('sales_draft__list_view__filter_customers'),
            type: 'checkbox',
            options: mergeFilterOptions(
                filterOptions?.customers.options,
                filterOptionsWithoutSearch?.customers.options,
                (o) => ({ id: o.option_id, label: o.option, value: o.option_id, count: o.drafts_quantity }),
                appliedFilters.customers,
            ),
        },
    ];

    const quickFilters: QuickFilter<DraftListQueryFilters>[] = useMemo(
        () => [
            {
                id: 'by_me',
                label: t('sales_draft__quick_filters__by_me'),
                filter: {
                    ...defaultDraftFilters,
                    users: [user.id],
                    states: ['NEW'],
                },
                showByDefault: true,
            },
            {
                id: 'submitted',
                label: t('sales_draft__quick_filters__submitted'),
                filter: {
                    ...defaultDraftFilters,
                    states: ['SUBMITTED'],
                },
                showByDefault: true,
            },
        ],
        [user, t],
    );

    const handleApplyFilters = (filters: DraftListQueryFilters) => {
        setAppliedFilters(filters);

        sendEvent('drafts_list_filter_used', getUsedFilterEventPayload(filters));
    };

    return (
        <VerticalFilterTemplate<DraftListQueryFilters>
            aclSubject="SalesDraft"
            viewKey="salesDraft.list"
            searchFilterKey="search_query"
            searchPlaceholder={t('sales_draft__list_view__search_placeholder')}
            filters={filtersConfig}
            onFiltersChange={setModalFilters}
            itemCount={filterOptions?.customers.totals.total || 0}
            isLoading={isLoading}
            defaultFilters={defaultDraftFilters}
            appliedFilters={appliedFilters}
            onApplyFilters={handleApplyFilters}
            quickFilters={quickFilters}
        />
    );
}
